import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractPlaceholderNames,
  parseTemplateDocument,
  renderTemplate,
  type FieldOptions,
  type TemplateDocument,
} from '@template-filler/core';
import { api } from '../api/client.js';
import { describeError } from '../lib/describe-error.js';
import { DocumentView } from '../render/DocumentView.js';
import { FilledResult } from './FilledResult.js';

export function FillForm(): JSX.Element {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [doc, setDoc] = useState<TemplateDocument | undefined>();
  // Feature 004: a field listed here is filled from a dropdown instead of a
  // text box.
  const [fieldOptions, setFieldOptions] = useState<FieldOptions>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState<string[]>([]);
  const [result, setResult] = useState<TemplateDocument | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;

    api
      .getTemplate(templateId)
      .then((template) => {
        if (cancelled) return;

        // Client-side read validation (FR-011). The server validates on read
        // too; doing it here as well means a document that somehow reached the
        // browser unvalidated still never gets rendered.
        const parsed = parseTemplateDocument(template.body);
        if (!parsed.ok) {
          setError(
            'This template is stored in a form this version cannot safely display. It has not been shown.',
          );
          return;
        }

        setTitle(template.title);
        setDoc(parsed.value);
        setFieldOptions(template.fieldOptions ?? {});
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(describeError(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [templateId]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!doc) return;

    const rendered = renderTemplate(doc, values);
    if (!rendered.ok) {
      // FR-013: name the fields that are missing rather than failing vaguely.
      setMissing(rendered.missing);
      return;
    }

    setMissing([]);
    setResult(rendered.value);
  }

  if (loading) return <p>Loading…</p>;

  if (error) {
    return (
      <main className="page">
        <p className="error" role="alert">
          {error}
        </p>
        <button type="button" className="btn-ghost" onClick={() => navigate('/')}>
          Back to library
        </button>
      </main>
    );
  }

  if (!doc) return <p>Template not found.</p>;

  if (result) {
    return <FilledResult title={title} doc={result} onBack={() => setResult(undefined)} />;
  }

  const fields = extractPlaceholderNames(doc);

  // FR-016: say there is nothing to fill rather than showing an empty form.
  if (fields.length === 0) {
    return (
      <main className="page">
        <header className="page-header">
          <h1>{title}</h1>
          <button type="button" className="btn-ghost" onClick={() => navigate('/')}>
            Back to library
          </button>
        </header>
        <p>This template has no fields, so there is nothing to fill in. Here it is as written:</p>
        <DocumentView doc={doc} />
        <button type="button" className="btn-primary" onClick={() => setResult(doc)}>
          Copy it
        </button>
      </main>
    );
  }

  return (
    <main className="page">
      <header className="page-header">
        <h1>Fill “{title}”</h1>
        <button type="button" className="btn-ghost" onClick={() => navigate('/')}>
          Back to library
        </button>
      </header>

      {missing.length > 0 ? (
        <p className="error" role="alert">
          Fill in {missing.length === 1 ? 'this field' : 'these fields'} before continuing:{' '}
          {missing.join(', ')}.
        </p>
      ) : null}

      <form onSubmit={submit} noValidate>
        {fields.map((name) => {
          const isMissing = missing.includes(name);
          const options = fieldOptions[name];
          const control =
            options && options.length > 0 ? (
              // Constrained field: a single-select of exactly the author's
              // values. It starts unselected, so an unchosen dropdown is reported
              // missing just like an empty text field (FR-003, FR-004, FR-006).
              <select
                id={`field-${name}`}
                value={values[name] ?? ''}
                aria-invalid={isMissing}
                {...(isMissing ? { 'aria-describedby': `field-${name}-error` } : {})}
                onChange={(event) =>
                  setValues((current) => ({ ...current, [name]: event.target.value }))
                }
              >
                <option value="" disabled>
                  Select…
                </option>
                {options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`field-${name}`}
                value={values[name] ?? ''}
                aria-invalid={isMissing}
                {...(isMissing ? { 'aria-describedby': `field-${name}-error` } : {})}
                onChange={(event) =>
                  setValues((current) => ({ ...current, [name]: event.target.value }))
                }
              />
            );

          return (
            <div className="field" key={name}>
              <label htmlFor={`field-${name}`}>{name}</label>
              {control}
              {isMissing ? (
                <span className="error" id={`field-${name}-error`}>
                  Required
                </span>
              ) : null}
            </div>
          );
        })}

        <button type="submit" className="btn-primary">
          Create document
        </button>
      </form>
    </main>
  );
}
